import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type WorkQueueBucket } from '@aps/contracts';
import { Button, EmptyState, QueueGroup, Skeleton } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { bucketLabel, messages, waitingText } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';

interface WorkQueueScreenProps {
  onOpenBucket: (bucket: WorkQueueBucket) => void;
}

/**
 * Ish navbati — admin panelning asosiy ekrani.
 *
 * Buyurtma ro'yxati EMAS, vazifa turi bo'yicha navbat
 * (docs/02-MAHSULOT.md → "Admin ish navbati").
 *
 * Guruhlar ro'yxati SERVERDAN keladi: ruxsati yetmagan guruh javobga
 * umuman kirmaydi, shuning uchun klientda ruxsat filtri takrorlanmaydi.
 */
export function WorkQueueScreen({ onOpenBucket }: WorkQueueScreenProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const summary = useQuery({
    queryKey: ['work-queue'],
    queryFn: ({ signal }) => api.workQueueSummary(signal),
    // Operator ekranni ochiq qoldiradi — sanoq eskirib qolmasin.
    refetchInterval: 60_000,
  });

  if (summary.isPending) {
    return (
      <section className="adm-page">
        <header className="adm-page__header">
          <h1 className="adm-page__title">{t.workQueue}</h1>
        </header>
        <div className="adm-queue-grid">
          <Skeleton height="72px" />
          <Skeleton height="72px" />
          <Skeleton height="72px" />
        </div>
      </section>
    );
  }

  if (summary.isError) {
    return (
      <section className="adm-page">
        <QueueError
          message={summary.error instanceof ApiError ? summary.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void summary.refetch()}
        />
      </section>
    );
  }

  const now = new Date(summary.data.generatedAt).getTime();
  // Bo'sh guruhlar ko'rsatilmaydi: nol yozilgan o'nta kartochka
  // orasidan ish borini topish qiyin.
  const active = summary.data.buckets.filter((bucket) => bucket.count > 0);

  return (
    <section className="adm-page">
      <header className="adm-page__header">
        <h1 className="adm-page__title">{t.workQueue}</h1>
        <Button variant="ghost" size="sm" onClick={() => void summary.refetch()}>
          {t.refresh}
        </Button>
      </header>

      {active.length === 0 ? (
        <EmptyState title={t.workQueueEmpty} />
      ) : (
        <div className="adm-queue-grid">
          {active.map((bucket) => (
            <div className="adm-queue-cell" key={bucket.bucket}>
              <QueueGroup
                label={bucketLabel(bucket.bucket, locale)}
                count={bucket.count}
                urgent={bucket.bucket === 'ETA_OVERDUE' || bucket.bucket === 'EXCEPTION'}
                onSelect={() => {
                  onOpenBucket(bucket.bucket);
                }}
              />
              {bucket.oldestWaitingSince !== null && (
                <span className="adm-queue-cell__meta">
                  {t.oldest}: {waitingText(bucket.oldestWaitingSince, now, locale)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

interface QueueErrorProps {
  message: string;
  retryLabel: string;
  onRetry: () => void;
}

export function QueueError({ message, retryLabel, onRetry }: QueueErrorProps): ReactNode {
  return (
    <div className="aps-error" role="alert">
      <p>{message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {retryLabel}
      </Button>
    </div>
  );
}
